/**
 * Contract line #30 (`POST /api/match/venue-run`, ADDITIVE v1.8.4, T.Z34) — the
 * «Обновить связи» button of the venue card's «Адреса» section.
 *
 * The call ARMS a venue-matcher pass and answers **202** `{data:{queued,runId}}`; the
 * worker's venue matcher (T.D2) walks the UNMATCHED queue on its next loop beat. The
 * 202 is therefore the ask, never the outcome — the same optimism rule
 * `requestSnapshotRefresh` documents for «Обновить данные» — and a second click while a
 * pass runs answers the SAME 202 (the worker holds the in-flight flag, there is no 409).
 * Progress is read from `sync_state MATCHER`, which the venues screens do not render;
 * the card only confirms that the pass was queued.
 */

import { useMutation } from '@tanstack/react-query';

import { API_BASE_URL, parseErrorBody, type ApiErrorCode } from './client';

export const VENUE_MATCH_RUN_PATH = '/match/venue-run';

/** The 202 body — the mirror of `VenueMatchRunDto` (`apps/api/src/match/dto.ts`). */
export interface VenueMatchRunDto {
  queued: boolean;
  runId: number;
}

/** A refused arming (401/403/500), re-thrown with the documented code. */
export class VenueMatchRunError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'VenueMatchRunError';
  }
}

export async function requestVenueMatchRun(signal?: AbortSignal): Promise<VenueMatchRunDto> {
  const response = await fetch(`${API_BASE_URL}${VENUE_MATCH_RUN_PATH}`, { method: 'POST', signal });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON — `parseErrorBody` falls back to the status-derived code.
    }
    const { code, message } = parseErrorBody(body, response.status);
    throw new VenueMatchRunError(response.status, code, message);
  }

  const payload = (await response.json()) as Record<string, unknown>;
  const data = payload['data'];
  // A body without the envelope still reads as "queued" — an older api stub must
  // not turn a 202 into a rendered error.
  return data && typeof data === 'object'
    ? (data as VenueMatchRunDto)
    : { queued: true, runId: Number(payload['runId'] ?? 0) };
}

export function useVenueMatchRun() {
  return useMutation({ mutationFn: () => requestVenueMatchRun() });
}
